import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { useState, FormEvent, ChangeEvent } from 'react';
import moment from 'moment';
import { useUser } from 'app/state/contexts/UserContext';
import { Modal, IconButton, NavbarDefault, DraggableTable } from 'app/components';
import { useCreateOrganization } from 'entities/organization';

interface Organization {
  id: number;
  name: string;
  createdAt?: string;
}

interface CreateOrganizationProps {
  closeModal?: () => void;
}

const CreateOrganization = ({ closeModal }: CreateOrganizationProps) => {
  const { t } = useTranslation('common');

  const { refreshOrganizations } = useUser();
  const [name, setName] = useState('');
  const { mutate: createOrganization } = useCreateOrganization();

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    createOrganization(
      { name },
      {
        onSuccess: () => {
          refreshOrganizations?.();
          setName('');
          closeModal?.();
        },
      }
    );
  };

  return (
    <form onSubmit={handleSubmit}>
      <input className="form-control" type="text" value={name} onChange={(e: ChangeEvent<HTMLInputElement>) => setName(e.target.value)} />

      <div className="mt-4 d-flex justify-content-end">
        <button className="button clear" type="button" onClick={closeModal}>
          {t('cancel')}
        </button>
        <button className="button">{t('save')}</button>
      </div>
    </form>
  );
};

export const Organizations = () => {
  const { t } = useTranslation('organisations');

  const { selectedOrganization, organizations, selectOrganization } = useUser();
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  const orgs = organizations as Organization[] | undefined;
  const items = (orgs ?? []).map((organization) => ({
    title: organization.name,
    detail: moment(organization.createdAt).format('YYYY-MM-DD HH:mm:ss'),
    onClick: () => {
      navigate({ to: '/' }).then(() => selectOrganization?.(organization.id));
    },
  }));

  return (
    <div className="project--details grid--container full--height columns">
      <div className="content">
        <div className="content-title">
          <h1>{orgs?.find((organization) => organization.id === selectedOrganization)?.name}</h1>
        </div>
        <div className="project--overview">
          <div className="columns two mb-2">
            <h2>{t('list.title')}</h2>
            <div className="action--align--left">
              <IconButton onClick={() => setShowModal(true)} icon="plus" />
            </div>
          </div>
          <div>
            <DraggableTable items={items} />
          </div>
        </div>
      </div>
      <NavbarDefault />
      <Modal titleLabelKey="organisations:new-org.modal.title" show={showModal} onClose={() => setShowModal(false)}>
        <CreateOrganization closeModal={() => setShowModal(false)} />
      </Modal>
    </div>
  );
};
